/**
 * Vorlagen: eigene Vorlagen und die Galerie fertiger Gestaltungen.
 *
 * Ein leeres Blatt ist die größte Hürde. Deshalb steht die Galerie oben:
 * sechs fertige Newsletter mit echten Bildern der Kanzlei, die nur noch
 * Texte brauchen. Wer lieber frei baut, nimmt das leere Grundgerüst.
 *
 * Der KI-Entwurf sitzt bewusst daneben und nicht davor — er ist eine
 * Einstiegshilfe, kein Automat. Bei einer Kanzlei hängt an jeder fachlichen
 * Aussage eine Haftung.
 */

import { Vorlagen, KI, Status } from '../api.js';
import {
  el,
  ersetzen,
  karte,
  knopf,
  meldung,
  eingabefeld,
  dialog,
  laedt,
  zeitpunkt,
} from '../ui.js';

/**
 * Eine leere Fläche zum Anfangen.
 *
 * Nicht wirklich leer: ein Textbaustein steht darin, weil eine vollständig
 * leere Fläche im Editor kein Ziel zum Ablegen bietet — man sähe nichts und
 * bekäme den ersten Baustein nicht hinein.
 */
const LEERE_VORLAGE = `<mjml>
  <mj-body width="600px">
    <mj-section>
      <mj-column>
        <mj-text>Hier beginnt Ihr Text. Bausteine ziehen Sie links aus der Auswahl herein.</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

/**
 * Öffnet den Editor mit einer Vorlage aus der Galerie.
 * @param {any} vorlage
 */
function galerieOeffnen(vorlage) {
  // Über den Sitzungsspeicher, damit die Adresszeile sauber bleibt und ein
  // Neuladen nicht versehentlich eine zweite Vorlage anlegt.
  sessionStorage.setItem('galerie-mjml', vorlage.mjml);
  sessionStorage.setItem('galerie-name', vorlage.name);
  sessionStorage.setItem('galerie-betreff', vorlage.betreff ?? '');
  location.hash = '#/editor';
}

/**
 * Ein bestehendes Design importieren.
 *
 * Der Weg, den eine Kanzlei beim Wechsel zuerst sucht: „Ich habe doch schon
 * Newsletter." Ohne ihn beginnt jede Vorlage bei null, und das alte Aussehen
 * wäre nur von Hand nachzubauen.
 *
 * Die beiden Aufnahmearten stehen zur Wahl, weil beide Wünsche berechtigt und
 * unvereinbar sind: das Aussehen behalten ODER frei bearbeiten können.
 */
function designImportDialog() {
  const dateiFeld = /** @type {HTMLInputElement} */ (
    el('input', {
      type: 'file',
      accept: '.eml,.zip,.html,.htm,.mjml,text/html,message/rfc822,application/zip',
    })
  );

  const { feld: codeFeld, eingabe: codeEingabe } = eingabefeld({
    beschriftung: '… oder den Code hier einfügen',
    art: 'textarea',
    platzhalter: '<html> … </html>',
    hinweis:
      'Im alten Werkzeug meist unter „Quellcode anzeigen" oder als HTML-Datei zu exportieren. ' +
      'Eine bereits fertige MJML-Vorlage wird ebenfalls erkannt.',
  });

  /**
   * Eine gewählte .eml oder .zip wird als Ganzes übergeben, nicht als Text
   * eingelesen: beide enthalten die Bilder, und die überleben den Umweg über
   * ein Textfeld nicht.
   * @type {string|null}
   */
  let dateiBase64 = null;
  /** @type {string} */
  let dateiName = '';

  const { feld: artFeld, eingabe: artEingabe } = eingabefeld({
    beschriftung: 'Wie soll importiert werden?',
    art: 'select',
    auswahl: [
      { wert: 'originalgetreu', text: 'Aussehen behalten (Anordnung bleibt fest)' },
      { wert: 'bausteine', text: 'In Bausteine umwandeln (frei bearbeitbar, neues Aussehen)' },
    ],
    wert: 'originalgetreu',
    hinweis:
      'Aussehen behalten: die Nachricht sieht aus wie bisher, Texte und Bilder lassen sich ' +
      'ändern. In Bausteine umwandeln: Inhalte werden herausgelöst und in die Kanzlei-Gestaltung ' +
      'gesetzt — alles frei bearbeitbar, das alte Layout entfällt.',
  });

  // Standardmäßig an: eine Vorlage, deren Bilder beim alten Anbieter liegen,
  // funktioniert nur so lange, bis dort jemand aufräumt. Danach zeigt jede
  // Aussendung leere Flächen — und im Editor sieht es weiter richtig aus,
  // weil der Browser die Bilder noch im Zwischenspeicher hat.
  const bilderFeld = /** @type {HTMLInputElement} */ (
    el('input', { type: 'checkbox', id: 'bilder-holen', checked: true })
  );

  const bilderZeile = el('div.feld', [
    el('label', { for: 'bilder-holen', style: 'display:flex;gap:.6rem;align-items:center' }, [
      bilderFeld,
      el('span', { text: 'Bilder in die Bildablage der Kanzlei holen' }),
    ]),
    el('p.feld-hinweis', {
      text: 'Empfohlen. Sonst bleiben die Bilder beim bisherigen Anbieter — verschwinden sie dort, sind die Aussendungen leer.',
    }),
  ]);

  const ergebnisBereich = el('div');

  dateiFeld.addEventListener('change', async () => {
    const datei = dateiFeld.files?.[0];
    if (!datei) return;
    dateiBase64 = null;
    dateiName = '';

    // .eml und .zip tragen ihre Bilder mit sich und gehen unverändert an den
    // Server. Alles andere ist Text und landet im Codefeld, wo es vor dem
    // Importieren noch zu sehen ist.
    const istArchiv = /\.zip$/i.test(datei.name) || datei.type === 'application/zip';
    const istEml = /\.eml$/i.test(datei.name) || datei.type === 'message/rfc822';

    if (istArchiv || istEml) {
      const puffer = new Uint8Array(await datei.arrayBuffer());
      let binaer = '';
      // Blockweise, weil ein einzelner Aufruf mit vielen tausend Zeichen den
      // Aufrufstapel sprengt - eine Aussendung mit Bildern erreicht das leicht.
      for (let i = 0; i < puffer.length; i += 8192) {
        binaer += String.fromCharCode(...puffer.subarray(i, i + 8192));
      }
      dateiBase64 = btoa(binaer);
      dateiName = datei.name;
      codeEingabe.value = '';
      ersetzen(
        ergebnisBereich,
        meldung(
          'info',
          `„${datei.name}" gewählt (${Math.round(datei.size / 1024)} kB). ` +
            (istArchiv
              ? 'Die Bilder aus dem Archiv werden in die Bildablage der Kanzlei übernommen.'
              : 'Betreff und eingebettete Bilder werden mit übernommen.'),
        ),
      );
      return;
    }

    codeEingabe.value = await datei.text();
    ersetzen(
      ergebnisBereich,
      meldung('info', `„${datei.name}" eingelesen (${Math.round(datei.size / 1024)} kB).`),
    );
  });

  const importieren = async () => {
    const quelle = codeEingabe.value.trim();
    if (!quelle && !dateiBase64) {
      ersetzen(
        ergebnisBereich,
        meldung('warnung', 'Bitte eine Datei auswählen oder den Code einfügen.'),
      );
      return;
    }

    const art = /** @type {'originalgetreu'|'bausteine'} */ (artEingabe.value);
    ersetzen(
      ergebnisBereich,
      laedt(bilderFeld.checked ? 'Design wird importiert, Bilder werden geholt …' : 'Design wird importiert …'),
    );
    try {
      const antwort = await Vorlagen.uebernehmen({
        ...(dateiBase64 ? { dateiBase64 } : { quelle }),
        art,
        bilderHolen: bilderFeld.checked,
      });
      const { mjml, bericht } = antwort;
      /** @type {string} */
      const betreff = antwort.betreff ?? '';

      const zaehler = (anzahl, einzahl, mehrzahl) => `${anzahl} ${anzahl === 1 ? einzahl : mehrzahl}`;

      const teile = [
        meldung(
          'erfolg',
          bericht.erkanntAls === 'mjml'
            ? 'Als fertige MJML-Vorlage erkannt und unverändert übernommen.'
            : art === 'originalgetreu'
              ? `Design übernommen — Aussehen bleibt erhalten, ${zaehler(bericht.bilder, 'Bild', 'Bilder')} gefunden.`
              : `Übernommen: ${zaehler(bericht.textbloecke, 'Textblock', 'Textblöcke')}, ` +
                `${zaehler(bericht.bilder, 'Bild', 'Bilder')}, ` +
                `${zaehler(bericht.schaltflaechen, 'Schaltfläche', 'Schaltflächen')}.`,
        ),
      ];

      if (betreff) {
        teile.push(meldung('info', `Betreff aus der Nachricht übernommen: „${betreff}"`));
      }

      // Entferntes zuerst: es ist die Änderung, mit der niemand rechnet.
      if (bericht.entfernt.length) {
        teile.push(
          meldung(
            'warnung',
            `Aus Sicherheitsgründen entfernt: ${bericht.entfernt.join(', ')}. ` +
              'In einer E-Mail hätte das ohnehin nicht funktioniert.',
          ),
        );
      }
      for (const hinweis of bericht.hinweise) {
        teile.push(meldung('info', hinweis));
      }

      teile.push(
        el('div.knopf-reihe', { style: 'margin-top:1rem' }, [
          knopf('Im Editor öffnen', {
            art: 'haupt',
            beiKlick: () => {
              // Derselbe Weg wie bei der Galerie: über den Sitzungsspeicher,
              // damit ein Neuladen keine zweite Vorlage anlegt.
              sessionStorage.setItem('galerie-mjml', mjml);
              sessionStorage.setItem(
                'galerie-name',
                dateiName ? dateiName.replace(/\.(eml|zip)$/i, '') : 'Übernommenes Design',
              );
              sessionStorage.setItem('galerie-betreff', betreff);
              schliessen();
              location.hash = '#/editor';
            },
          }),
        ]),
      );

      ersetzen(ergebnisBereich, ...teile);
    } catch (fehler) {
      ersetzen(
        ergebnisBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Übernahme fehlgeschlagen'),
      );
    }
  };

  const schliessen = dialog({
    titel: 'Bestehendes Design importieren',
    breit: true,
    inhalt: [
      el('p', {
        text: 'Einen bestehenden Newsletter aufnehmen — statt ihn neu zu bauen. Am einfachsten ist eine versendete Nachricht: in Outlook „Speichern unter" wählen, dann liegt sie als .eml-Datei vor, samt Betreff und eingebetteten Bildern.',
      }),
      el('div.feld', [
        el('label', { text: 'E-Mail (.eml), HTML- oder MJML-Datei' }),
        dateiFeld,
        el('p.feld-hinweis', {
          text: 'Aus einer .eml werden Betreff und eingebettete Bilder mit übernommen; die Bilder landen in der Bildablage der Kanzlei.',
        }),
      ]),
      codeFeld,
      artFeld,
      bilderZeile,
      ergebnisBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { beiKlick: () => schliessen() }),
      knopf('Importieren', { art: 'haupt', beiKlick: importieren }),
    ],
  });
}

/**
 * Dialog für den KI-Entwurf.
 * @param {() => void} neuLaden
 */
function kiAssistent(neuLaden) {
  const { feld: themaFeld, eingabe: themaEingabe } = eingabefeld({
    beschriftung: 'Worum geht es?',
    platzhalter: 'z. B. Erinnerung an die Arbeitnehmerveranlagung',
    hinweis: 'Je genauer die Angabe, desto brauchbarer der Entwurf.',
  });

  const { feld: zielgruppeFeld, eingabe: zielgruppeEingabe } = eingabefeld({
    beschriftung: 'An wen richtet sich der Newsletter?',
    platzhalter: 'z. B. Einzelunternehmer und kleine GmbHs',
  });

  const ergebnisBereich = el('div');

  const erzeugen = async () => {
    if (!themaEingabe.value.trim()) return;
    ersetzen(ergebnisBereich, laedt('Entwurf wird geschrieben — das dauert etwa eine Minute …'));
    try {
      const entwurf = await KI.entwurf({
        thema: themaEingabe.value.trim(),
        ...(zielgruppeEingabe.value.trim() ? { zielgruppe: zielgruppeEingabe.value.trim() } : {}),
      });

      const importieren = async () => {
        const vorlage = await Vorlagen.anlegen({
          name: `Entwurf: ${themaEingabe.value.trim().slice(0, 40)}`,
          mjml: entwurf.mjml,
          betreff: entwurf.betreff,
        });
        neuLaden();
        location.hash = `#/editor/${vorlage.id}`;
      };

      ersetzen(
        ergebnisBereich,
        meldung(
          'warnung',
          'Der Entwurf ist ein Ausgangspunkt. Alle fachlichen Aussagen, Fristen und Beträge müssen vor dem Versand geprüft werden.',
        ),
        el('div.feld', [
          el('label', { text: 'Vorgeschlagener Betreff' }),
          el('p', { text: entwurf.betreff }),
        ]),
        entwurf.hinweise?.length
          ? el('div.feld', [
              el('label', { text: 'Vor dem Versand zu prüfen' }),
              el(
                'ul',
                entwurf.hinweise.map((/** @type {string} */ h) => el('li', { text: h })),
              ),
            ])
          : null,
        entwurf.blockiert
          ? meldung(
              'fehler',
              'Der Entwurf hat Darstellungsfehler und muss im Editor überarbeitet werden.',
            )
          : null,
        el('div.knopf-reihe', [
          knopf('Als Vorlage übernehmen und öffnen', { art: 'haupt', beiKlick: uebernehmen }),
        ]),
      );
    } catch (fehler) {
      ersetzen(
        ergebnisBereich,
        meldung('fehler', fehler instanceof Error ? fehler.message : 'Entwurf fehlgeschlagen'),
      );
    }
  };

  dialog({
    titel: 'Entwurf vorschlagen lassen',
    inhalt: [
      el('p', {
        text: 'Der Assistent schreibt einen Newsletter in der Gestaltung und Sprache der Kanzlei. Sie überarbeiten ihn anschließend im Editor.',
      }),
      themaFeld,
      zielgruppeFeld,
      ergebnisBereich,
    ],
    aktionen: [knopf('Entwurf erzeugen', { art: 'haupt', beiKlick: erzeugen })],
  });
}

/**
 * Fragt nach und löscht dann.
 *
 * Die Rückfrage ist hier nicht Zierde: eine Vorlage ist Arbeit von einer
 * Stunde, und der Knopf sitzt neben „Bearbeiten".
 *
 * @param {{ id: number, name: string }} vorlage
 * @param {() => Promise<void>} neuLaden
 */
function vorlageLoeschen(vorlage, neuLaden) {
  const meldungsBereich = el('div');

  const schliessen = dialog({
    titel: 'Vorlage löschen',
    inhalt: [
      el('p', { text: `Soll die Vorlage „${vorlage.name}" gelöscht werden?` }),
      el('p.editor-hinweis', {
        text: 'Wird sie von einer Aussendung verwendet, bleibt sie erhalten — sonst wäre nicht mehr nachvollziehbar, was damals versendet wurde.',
      }),
      meldungsBereich,
    ],
    aktionen: [
      knopf('Abbrechen', { beiKlick: () => schliessen() }),
      knopf('Löschen', {
        art: 'haupt',
        beiKlick: async () => {
          ersetzen(meldungsBereich, laedt('Wird gelöscht …'));
          try {
            await Vorlagen.loeschen(vorlage.id);
            schliessen();
            await neuLaden();
          } catch (fehler) {
            ersetzen(
              meldungsBereich,
              meldung('warnung', fehler instanceof Error ? fehler.message : 'Löschen nicht möglich'),
            );
          }
        },
      }),
    ],
  });
}

/**
 * @returns {import('../app.js').Ansicht}
 */
export function vorlagenAnsicht() {
  return {
    titel: 'Vorlagen',
    async zeichnen(behaelter) {
      const eigeneBereich = el('div');

      const laden = async () => {
        ersetzen(eigeneBereich, laedt());
        const vorlagen = await Vorlagen.liste();

        if (!vorlagen.length) {
          ersetzen(
            eigeneBereich,
            el('p.leer', {
              text: 'Noch keine eigene Vorlage. Wählen Sie oben eine fertige Gestaltung — Sie tauschen dann nur noch die Texte aus.',
            }),
          );
          return;
        }

        ersetzen(
          eigeneBereich,
          el(
            'div.auswahl',
            vorlagen.map((vorlage) =>
              el(
                'button.auswahl-eintrag',
                {
                  type: 'button',
                  onclick: () => {
                    location.hash = `#/editor/${vorlage.id}`;
                  },
                },
                [
                  el('span', [
                    el('span.haupttext', { text: vorlage.name }),
                    el('span.nebentext', {
                      text: `${vorlage.betreff || 'Ohne Betreff'} · zuletzt bearbeitet ${zeitpunkt(vorlage.geaendert_am)}`,
                    }),
                  ]),
                  el('span.rechts', [
                    // Kopieren und Löschen sitzen in der Zeile, nicht in einem
                    // Menü: es sind zwei Aktionen, und ein Menü für zwei
                    // Einträge ist ein Klick zu viel.
                    el('button.still', {
                      type: 'button',
                      text: 'Kopie',
                      title: 'Als Kopie anlegen und dort weiterarbeiten',
                      onclick: async (/** @type {Event} */ ereignis) => {
                        // Sonst öffnet der Klick zusätzlich den Editor.
                        ereignis.stopPropagation();
                        const kopie = await Vorlagen.duplizieren(vorlage.id);
                        location.hash = `#/editor/${kopie.id}`;
                      },
                    }),
                    el('button.still', {
                      type: 'button',
                      text: 'Löschen',
                      title: 'Diese Vorlage löschen',
                      onclick: (/** @type {Event} */ ereignis) => {
                        ereignis.stopPropagation();
                        vorlageLoeschen(vorlage, laden);
                      },
                    }),
                    el('span', { text: 'Bearbeiten ›' }),
                  ]),
                ],
              ),
            ),
          ),
        );
      };

      const [status, galerie] = await Promise.all([
        Status.laden().catch(() => null),
        Vorlagen.galerie().catch(() => []),
      ]);

      const galerieRaster = el(
        'div.galerie',
        galerie.map((vorlage) =>
          el(
            'button.vorlagen-karte',
            { type: 'button', onclick: () => galerieOeffnen(vorlage) },
            [
              el('div.vorlagen-bild', [
                el('img', { src: vorlage.vorschaubild, alt: '', loading: 'lazy' }),
              ]),
              el('div.vorlagen-text', [
                el('span.titel', { text: vorlage.name }),
                el('span.beschreibung', { text: vorlage.beschreibung }),
              ]),
            ],
          ),
        ),
      );

      ersetzen(
        behaelter,
        el('div.bahn', [
          el('div.seiten-kopf', [
            el('div', [
              el('h1', { text: 'Vorlagen' }),
              el('p', {
                text: 'Die Gestaltung Ihrer Newsletter. Eine Vorlage lässt sich für beliebig viele Aussendungen verwenden.',
              }),
            ]),
            el('div.knopf-reihe', [
              // Steht vor „Neue Vorlage": wer schon Newsletter hat, sucht
              // diesen Weg zuerst — und fand ihn bisher gar nicht.
              knopf('Design importieren', {
                titel: 'Einen bestehenden Newsletter aus HTML, MJML oder einer E-Mail aufnehmen',
                beiKlick: () => designImportDialog(),
              }),
              status?.kiAssistent
                ? knopf('Entwurf vorschlagen lassen', { beiKlick: () => kiAssistent(laden) })
                : null,
              // Der naheliegendste Weg gehört an die naheliegendste Stelle:
              // oben rechts, wo bei den Aussendungen „Neue Aussendung" steht.
              // Vorher führte der einzige Weg zu einer leeren Vorlage über die
              // sechste Karte der Galerie, ganz unten — wer einfach anfangen
              // wollte, fand ihn nicht.
              // Ganz ohne Gerüst anfangen. Es steht neben „Neue Vorlage" und
              // nicht darin versteckt, weil beides berechtigte Ausgangspunkte
              // sind: das Gerüst spart Arbeit, die leere Fläche gibt Freiheit.
              // Ohne Fußzeile fehlt zunächst der Abmeldelink — die Prüfung
              // sagt das und bietet an, ihn einzusetzen.
              knopf('Leer beginnen', {
                titel: 'Mit einer leeren Fläche anfangen — ohne Kopf, Anrede und Fußzeile',
                beiKlick: () => {
                  sessionStorage.setItem('galerie-mjml', LEERE_VORLAGE);
                  sessionStorage.setItem('galerie-name', 'Neue Vorlage');
                  sessionStorage.setItem('galerie-betreff', '');
                  location.hash = '#/editor';
                },
              }),
              knopf('Neue Vorlage', {
                art: 'haupt',
                titel: 'Mit dem Kanzlei-Gerüst anfangen: Logo, Anrede und Fußzeile mit Abmeldelink',
                beiKlick: () => {
                  // Ohne Kennung startet der Editor mit dem Grundgerüst:
                  // Logo, Anrede, Fußzeile mit Abmeldelink.
                  sessionStorage.removeItem('galerie-mjml');
                  location.hash = '#/editor';
                },
              }),
            ]),
          ]),

          karte({
            titel: 'Fertige Gestaltungen',
            hinweis:
              'Wählen Sie eine aus — Logo, Farben, Abstände und Abmeldelink sind bereits richtig gesetzt. Sie tauschen nur noch die Texte aus.',
            inhalt: galerie.length
              ? galerieRaster
              : meldung('warnung', 'Die Galerie konnte nicht geladen werden.'),
          }),

          karte({
            titel: 'Ihre Vorlagen',
            inhalt: eigeneBereich,
          }),
        ]),
      );

      await laden();
    },
  };
}
